import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import FileSaver from 'file-saver';
import { Column, Row, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import {
    Box,
    Center,
    Flex,
    useDisclosure,
    Icon,
    IconButton,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Badge,
    Text,
    Checkbox,
    useToast,
    Tag,
    TagLeftIcon,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
} from '@chakra-ui/react';

import { HEADER_USER, HIDDEN_COLUMN, PREFERED_COLUMN_ORDER, USER_FIELDS } from 'pages/UserManagement/const';
import { ICompanyGroups, IUserGroup, TCompanyWithCompanyGroupsDict, TUser } from 'pages/UserManagement/types';

import {
    useLazyExportUsersQuery,
    useGetCompanyGroupQuery,
    useGetInternationalCompanyQuery,
    useGetInternationalUsersQuery,
    useGetUsersV2Query,
    useGetCompanyGroupsByCompanyQuery,
} from 'core/API/usersApi';

import { useGetCompanyInfoSelector, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import { DITable } from 'plugins/DITable';
import GlobalFilter from 'plugins/DITable/filters/GlobalFilter';
import TablePagination from 'plugins/DITable/Pagination';
import { tableToolbarStyle, iconTableStyle, linkStyle } from 'shared/styles';
import icons from 'shared/icons';
import { EXCEL_FILE_TYPE } from 'shared/const';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import SelectColumnFilter from 'plugins/DITable/filters/SelectFilter';
import BooleanColumnFilter from 'plugins/DITable/filters/BooleanColumnFilter';
import { Dictionary, keyBy } from 'lodash';

import MUserEdit from '../MUserEdit/UserEdit';
import {
    useGetAllInternationalCompaniesQuery,
    useGetInternationalCompanyGroupsQuery,
} from 'core/API/internationalCompanyGroupApi';
import { IinternationalCompanies, IInternationalCompanyGroup } from 'types';

const initialState = {
    hiddenColumns: [...HIDDEN_COLUMN],
};

const MUsersViewer = () => {
    const { data: users, isError, isLoading } = useGetUsersV2Query();
    const [showInternationalUsers, setShowInternationalUsers] = useState<boolean>(false);

    const {
        data: internationalUsers,
        isError: isInternationalUsersError,
        isLoading: isInternationalUsersLoading,
    } = useGetInternationalUsersQuery();

    const {
        data: userCompanyGroupsData,
        isLoading: isGetCompanyGroupLoading,
        isError: isGetCompanyGroupError,
    } = useGetCompanyGroupQuery();

    const {
        data: companyGroupsByCompanyData,
        isLoading: isGetCompanyGroupsByCompanyLoading,
        isError: isGetCompanyGroupsByCompanyError,
    } = useGetCompanyGroupsByCompanyQuery();

    const {
        data: userInternationalCompanyData,
        isLoading: isGetInternationalCompanyLoading,
        isError: isGetInternationalCompanyError,
    } = useGetInternationalCompanyQuery();
    const DownloadIcon = icons.download;

    const [isAdd, setIsAdd] = useState<boolean>(true);
    const [showActiveUsers, setShowUsers] = useState<boolean>(false);
    const [userData, setUserData] = useState<TUser>();
    const [userList, setUserList] = useState<TUser[]>([]);
    const [exportTrigger, { isLoading: isExportLoading }] = useLazyExportUsersQuery();
    const [companyGroupsByCompanyId, setCompanyGroupsByCompanyId] = useState<TCompanyWithCompanyGroupsDict>({});
    const [internationalCompanies, setInternationalCompanies] = useState<Record<string, { id: string; name: string }>>(
        {},
    );

    const [internationalCompaniesById, setInternationalCompaniesById] = useState<Dictionary<IinternationalCompanies>>(
        {},
    );

    const {
        data: internationalComapnyGroups,
        isLoading: isGetInternationalCompanyGroupsLoading,
        isError: isGetInternationalCompanyGroupsError,
    } = useGetInternationalCompanyGroupsQuery();
    const [internationalCompanyGroupsById, setInternationalCompanyGroupsById] = useState<
        Dictionary<IInternationalCompanyGroup>
    >({});
    const {
        data: allInternationalCompanies,
        isLoading: isAllInternationalCompaniesLoading,
        isError: isAllInternationalCompaniesError,
    } = useGetAllInternationalCompaniesQuery();

    const toast = useToast();
    const companyInfo = useGetCompanyInfoSelector();
    const userInfo = useGetUserInfoSelector();
    const { isOpen: isImportOpen, onOpen: onImportOpen, onClose: onImportClose } = useDisclosure();
    const translate = useTranslation();

    const filtreAndOrderColumns = (tableHeader: string[]) => {
        const authorizedSort = [...PREFERED_COLUMN_ORDER];
        let headerArray = [...tableHeader];

        headerArray = authorizedSort
            .filter((elem) => headerArray.includes(elem))
            .concat(headerArray.filter((elem: string) => !authorizedSort.includes(elem)).sort());
        return headerArray;
    };

    useEffect(() => {
        if (companyGroupsByCompanyData && !isGetCompanyGroupsByCompanyLoading && !isGetCompanyGroupsByCompanyError) {
            const companiesWithFormattedCGs = companyGroupsByCompanyData.companies.map((cmp) => ({
                ...cmp,
                company_groups: keyBy(cmp.company_groups, 'id'),
            }));
            const companyGroupsByCompanyId = keyBy(companiesWithFormattedCGs, 'id');
            setCompanyGroupsByCompanyId(companyGroupsByCompanyId);
        }
    }, [isGetCompanyGroupsByCompanyLoading, isGetCompanyGroupsByCompanyError]);

    useEffect(() => {
        if (userInternationalCompanyData && !isGetInternationalCompanyLoading && !isGetInternationalCompanyError) {
            const groupMap: Record<string, any> = {};
            userInternationalCompanyData.internationalCompanies.forEach((group) => {
                groupMap[group.id] = group;
            });

            setInternationalCompanies(groupMap);
        }

        if (!userInternationalCompanyData && !isGetInternationalCompanyLoading && isGetInternationalCompanyError) {
            initialState.hiddenColumns = [...initialState.hiddenColumns, 'international_company'];
        }
    }, [isGetInternationalCompanyLoading, isGetInternationalCompanyError]);

    useEffect(() => {
        setInternationalCompaniesById(keyBy(allInternationalCompanies?.data, 'id'));
    }, [allInternationalCompanies]);

    useEffect(() => {
        if (!isLoading && !isError && users) {
            if (showInternationalUsers) {
                setUserList([])
                if (internationalUsers) {
                    const intCompanisById = internationalComapnyGroups ? keyBy(internationalComapnyGroups.data, 'id') : {};
                    const formattedInternationalUsers = internationalUsers.users.map(
                        (user) =>
                        ({
                            ...user,
                            international_company_group: {
                                id: user.international_company_group,
                                name:
                                    typeof user.international_company_group === 'number'
                                        ? intCompanisById[user.international_company_group]?.name
                                        : undefined,
                            },
                        } as TUser),
                    );

                    if (!showActiveUsers) {
                        setUserList(formattedInternationalUsers.filter((user) => user.is_active));
                    } else {
                        setUserList(formattedInternationalUsers);
                    }
                }
            } else {
                if (!showActiveUsers) {
                    setUserList(users.users.filter((user) => user.is_active));
                } else {
                    setUserList(users.users);
                }
            }
        }
    }, [
        isLoading,
        isError,
        users,
        internationalUsers,
        showActiveUsers,
        showInternationalUsers,
        internationalComapnyGroups,
    ]);

    const openModalAddUserData = () => {
        setIsAdd(true);
        onImportOpen();
    };

    const openModalWithUserData = (user: TUser) => {
        setIsAdd(false);
        setUserData(user);
        onImportOpen();
    };

    const renderUserEditModal = () => {
        return (
            <Modal useInert={false} isCentered isOpen={isImportOpen} onClose={onImportClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text as="h3">
                            {translate(`users.${isAdd ? 'add' : 'edit'}`)}
                            {showInternationalUsers
                                ? translate('user.international.edit.modal.title')
                                : translate('users.edit.modal.title')}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton mt="2" />
                    <ModalBody fontSize="sm">
                        <MUserEdit
                            isInternational={!isGetCompanyGroupError}
                            isAdd={isAdd}
                            values={!isAdd ? userData : ({} as TUser)}
                            onClose={onImportClose}
                            isInternationalUser={showInternationalUsers}
                            internationalCompanyGroups={internationalComapnyGroups?.data || []}
                            internationalCompanies={allInternationalCompanies?.data || []}
                            internationalCompaniesById={internationalCompaniesById}
                            companyGroupsByCompany={companyGroupsByCompanyId}
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    };

    const badgeSort = useCallback((rowA: Row, rowB: Row, columnId: string) => {
        const getString = (row: Row) => row.values[columnId].join(',').toLowerCase();
        return getString(rowA) > getString(rowB) ? 1 : -1;
    }, []);
    const columns: Readonly<Column<any>[]> = useMemo(() => {
        if (!isLoading && !isError && users) {
            const dynamicUserFields = showInternationalUsers
                ? USER_FIELDS
                : USER_FIELDS.filter((field) => field !== 'company' && field !== 'international_company_group');
            const filteredTableHeader: Column<any>[] = filtreAndOrderColumns(dynamicUserFields).map(
                (header: string) => {
                    switch (header) {
                        case 'username':
                            return {
                                Header: translate('users.email') as string,
                                accessor: header,
                                Cell: ({ row: { values, original } }) => {
                                    const userValues = values as TUser;
                                    return (
                                        <Box
                                            onClick={() =>
                                                openModalWithUserData({
                                                    ...original,
                                                    company: original.company_id,
                                                    international_company_group:
                                                        original.international_company_group?.id || null,
                                                })
                                            }
                                        >
                                            <Text {...linkStyle}>{userValues.username}</Text>
                                        </Box>
                                    );
                                },
                            };
                        case 'commercial_id':
                            return {
                                Header: translate('users.commercial_id') as string,
                                accessor: header,
                                Cell: ({ row: { values } }) => {
                                    const userValues = values as TUser;
                                    return (
                                        <Flex>
                                            <Text>
                                                {userValues.commercial_id == null ||
                                                    Number(userValues.commercial_id) === 0
                                                    ? ''
                                                    : userValues.commercial_id}
                                            </Text>
                                        </Flex>
                                    );
                                },
                            };
                        case 'is_active':
                            return {
                                Header: translate('users.is_active') as string,
                                accessor: header,
                                Filter: BooleanColumnFilter,
                                Cell: ({ row: { values } }) => {
                                    const userValues = values as TUser;
                                    return (
                                        <Flex>
                                            <Checkbox isChecked={userValues.is_active}></Checkbox>
                                        </Flex>
                                    );
                                },
                            };
                        case 'company_group':
                            return {
                                Header: translate('users.company_group') as string,
                                id: header,
                                accessor: (originalRow) => {
                                    if (
                                        companyGroupsByCompanyId[originalRow.company_id] &&
                                        companyGroupsByCompanyId[originalRow.company_id].company_groups[
                                        originalRow.company_group
                                        ]
                                    ) {
                                        return companyGroupsByCompanyId[originalRow.company_id].company_groups[
                                            originalRow.company_group
                                        ].name;
                                    }
                                    return '';
                                },
                                Filter: SelectColumnFilter,
                                Cell: ({ row: { values } }) => {
                                    const userValues = values as TUser;
                                    return (
                                        <Flex>
                                            <Badge colorScheme="blue" marginRight={2}>
                                                {userValues.company_group}
                                            </Badge>
                                        </Flex>
                                    );
                                },
                            };
                        case 'company':
                            return {
                                Header: translate('users.company') as string,
                                accessor: 'company_name',
                                Filter: SelectColumnFilter,
                                Cell: ({ row: { original } }) => {
                                    const userValues = original as TUser;
                                    return (
                                        <Flex>
                                            <Badge colorScheme="blue" marginRight={2}>
                                                {userValues.company_name}
                                            </Badge>
                                        </Flex>
                                    );
                                },
                            };

                        case 'international_company_group':
                            return {
                                Header: translate('users.international_company_group') as string,
                                accessor: (originalRow) => originalRow[header]?.name,
                                Filter: SelectColumnFilter,
                                Cell: ({ row: { original } }) => {
                                    const userValues = original as TUser;
                                    return (
                                        <Flex>
                                            {typeof userValues?.international_company_group === 'object' &&
                                                userValues?.international_company_group?.name}
                                        </Flex>
                                    );
                                },
                            };

                        case 'international_company':
                            return {
                                Header: translate('users.international_company') as string,
                                accessor: header,
                                disableFilters: true,
                                Cell: ({ row: { values } }) => {
                                    const userValues = values as TUser;
                                    return (
                                        <Flex>
                                            <Badge colorScheme="blue" marginRight={2}>
                                                {userValues.international_company &&
                                                    (showInternationalUsers
                                                        ? internationalCompaniesById[userValues.international_company]
                                                            ?.name
                                                        : internationalCompanies[userValues.international_company]
                                                            ?.name)}
                                            </Badge>
                                        </Flex>
                                    );
                                },
                            };
                        case 'groups':
                            return {
                                Header: translate('users.user_roles') as string,
                                id: header,
                                accessor: (originalRow) => originalRow[header]?.map(({ name }: IUserGroup) => name),
                                Filter: SelectColumnFilter,
                                filter: 'arrIncludes',
                                sortType: badgeSort,
                                Cell: ({ row: { values } }) => {
                                    const userValues = values;
                                    return (
                                        <Box maxW="3à0px" display="Flex" flexWrap="wrap">
                                            {userValues.groups?.map((groupName: string) => (
                                                <Badge colorScheme="blue" mr={2} mb={2} display="inline-block">
                                                    {groupName}
                                                </Badge>
                                            ))}
                                        </Box>
                                    );
                                },
                            };
                        case 'language':
                            return {
                                Header: translate('users.language') as string,
                                accessor: header,
                                Filter: SelectColumnFilter,
                            };
                        default:
                            const nd = HEADER_USER[header as keyof typeof HEADER_USER]
                                ? translate(HEADER_USER[header as keyof typeof HEADER_USER])
                                : header;

                            return {
                                Header: nd as string,
                                accessor: header,
                                minWidth: 200,
                                width: 200,
                            };
                    }
                },
            );

            return filteredTableHeader;
        }
        return [];
    }, [
        isLoading,
        isError,
        isInternationalUsersLoading,
        isInternationalUsersError,
        showInternationalUsers,
        internationalCompanies,
        internationalCompaniesById,
    ]);

    const defaultColumn = useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }),
        [],
    );

    const tableInstance = useTable(
        { data: userList, columns, defaultColumn, initialState },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
    );

    const renderTableToolbar = () => {
        const { state, setGlobalFilter } = tableInstance;
        const PlusIcon = icons.plus;
        const handleExportUsers = async (isInternational = false) => {
            try {
                const res = await exportTrigger({ isTemplate: false, isInternational });
                if ('error' in res) throw new Error('error.generic');

                const blob = new Blob([res.data], {
                    type: EXCEL_FILE_TYPE,
                });

                FileSaver.saveAs(blob, 'users.xlsx');
                toast({
                    title: translate('users.export.excel.success'),
                    status: 'success',
                    isClosable: true,
                });
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const handleCheckRow = (e: ChangeEvent<HTMLInputElement>) => {
            setShowUsers(!showActiveUsers);
        };

        const KeyIcon = icons.key;
        const maxUser = companyInfo ? companyInfo?.max_users : 1;
        const userlength = userList.filter((user) => user.is_active).length;
        const userPourcentage = (userlength / maxUser) * 100;

        return (
            <Flex {...tableToolbarStyle}>
                <GlobalFilter globalFilter={state.globalFilter} setGlobalFilter={setGlobalFilter} />

                <Menu>
                    <MenuButton
                        as={IconButton}
                        {...iconTableStyle}
                        isDisabled={isExportLoading}
                        aria-label="Download excel"
                        isLoading={isExportLoading}
                        size="sm"
                        icon={<Icon as={DownloadIcon} />}
                    />
                    <MenuList zIndex="modal">
                        <MenuItem onClick={intentionalyFloatingPromiseReturn(() => handleExportUsers(false))}>
                            {translate('users.local.download')}
                        </MenuItem>
                        <MenuItem onClick={intentionalyFloatingPromiseReturn(() => handleExportUsers(true))}>
                            {translate('users.international.download')}
                        </MenuItem>
                    </MenuList>
                </Menu>
                <IconButton
                    onClick={openModalAddUserData}
                    {...iconTableStyle}
                    aria-label="add user"
                    size="sm"
                    icon={<Icon as={PlusIcon} />}
                />
                <Checkbox ml={2} isChecked={showActiveUsers} onChange={(e) => handleCheckRow(e)}>
                    {translate('users.staff.inactive.user')}
                </Checkbox>
                {
                    userInfo?.is_international &&
                    <Checkbox
                        ml={2}
                        isChecked={showInternationalUsers}
                        onChange={(e) => setShowInternationalUsers((showActiveUsers) => !showActiveUsers)}
                    >
                        {translate('users.international.show')}
                    </Checkbox>
                }
                {companyInfo?.max_users && (
                    <Tag
                        ml={2}
                        size="sm"
                        bgColor={userPourcentage < 85 ? 'teal.200' : 'red.200'}
                        border="1px solid"
                        borderColor={userPourcentage < 85 ? 'teal.300' : 'red.300'}
                        color={userPourcentage < 85 ? 'teal.700' : 'red.700'}
                        mr="8px"
                    >
                        <TagLeftIcon as={KeyIcon} />
                        users: {userlength} / {companyInfo?.max_users} ({userPourcentage.toFixed(2)}%)
                    </Tag>
                )}
            </Flex>
        );
    };

    return (
        <Box mt="16px">
            {renderTableToolbar()}
            {columns.length !== 0 && <DITable {...tableInstance} isPagination />}
            {isLoading || (isInternationalUsersLoading && showInternationalUsers) ? (
                <Center w="100%" h="200px" bg="white" border="1px solid" borderColor="gray.100">
                    <Spinner />
                </Center>
            ) : (
                <TablePagination tableInstance={tableInstance} />
            )}
            {renderUserEditModal()}
        </Box>
    );
};

export default MUsersViewer;
